import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
  Tab,
  Tabs,
} from "react-bootstrap";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import { useNavigate, Link, useParams } from "react-router-dom";
import swal from "sweetalert";
import { validateCheckNumbers } from "../../../utils/checkNumberValidator";
import CheckNumberError from "../../../components/inputs/CheckNumberError";
import { customStyles } from "../../../assets/table-style";
import DataTable from "react-data-table-component";
// import { jwtDecode } from "jwt-decode";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import {
  format,
  parse as parseDateFns,
  isValid as isValidDateFns,
} from "date-fns";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import Select from "react-select";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import {
  currencyList,
  currencySymbol,
  truncateToTwoDecimals,
  parseNumber,
} from "../../../utils/numberFormatter";
import CustomDatePicker, {
  CustomDatePickerHeader,
} from "../../../components/CustomDatePicker";
import { usePostedCutoffValidation } from "../../../hooks/customHook/usePostedCutoffValidation";
import NumberInDecimals from "../../../components/inputs/NumberFormatted";
import BCPayments from "./components/BCPayments";
import useDarkMode from "../../../components/Themes/Usedarkmode";
import { warnIfNoCutoff } from "../../../utils/cutoffDateValidation";
import TypedDateInput, {
  DATE_INPUT_FORMAT,
} from "../../../components/inputs/TypedDateInput";

// Typed date input (Process Date / Issued Cheque Date) must follow MM/dd/yyyy
// (e.g. 10/08/2026). The input itself is the shared TypedDateInput (same as the
// Invoice Re-Open Date): slashes are added automatically; anything that is not
// a valid prefix of that format (letters, extra digits, other date formats) is
// rejected and the input keeps its previous text. maskTypedDate returns the
// masked text, or null when the keystroke was rejected.
// const DATE_INPUT_FORMAT = "MM/dd/yy"; // Replaced by DATE_INPUT_FORMAT from TypedDateInput (MM/dd/yyyy)

const maskTypedDate = (e) => {
  const raw = e?.target?.value;
  if (typeof raw !== "string") return null;

  const next = raw
    .replace(/^(\d{2})(\d)/, "$1/$2")
    .replace(/^(\d{2}\/\d{2})(\d)/, "$1/$2");

  // if (!/^(\d{0,2}|\d{2}\/\d{0,2}|\d{2}\/\d{2}\/\d{0,2})$/.test(next)) { // 2-digit year (MM/dd/yy)
  if (!/^(\d{0,2}|\d{2}\/\d{0,2}|\d{2}\/\d{2}\/\d{0,4})$/.test(next)) {
    e.preventDefault();
    return null;
  }
  e.target.value = next;
  return next;
};

// Typed text that is a complete MM/dd/yyyy date -> that day as a Date (start of
// day). Anything else (still typing, or an impossible date like 02/31/2026) -> null.
const parseCompleteTypedDate = (text) => {
  // if (!/^\d{2}\/\d{2}\/\d{2}$/.test(text)) return null; // 2-digit year (MM/dd/yy)
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return null;
  const typed = parseDateFns(text, DATE_INPUT_FORMAT, new Date());
  if (!isValidDateFns(typed)) return null;
  typed.setHours(0, 0, 0, 0);
  return typed;
};

const toDateOnly = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

// Shared by the picked/typed handlers so the wording lives in one place.
const warnProcessDateAfterMaturity = () =>
  swal({
    icon: "warning",
    title: "Invalid Process Date",
    text: "Process Date cannot be greater than the Maturity Date.",
    buttons: false,
    timer: 2500,
  });

const warnMaturityBeforeProcessDate = () =>
  swal({
    icon: "warning",
    title: "Invalid Maturity Date",
    text: "Maturity Date cannot be earlier than the Process Date.",
    buttons: false,
    timer: 2500,
  });

// react-select paints itself through emotion `styles` (white control and menu,
// dark-gray text), so the [data-theme="dark"] rules in style.css can't reach
// it. In dark mode, layer dark colors on top of whatever selectCustomStyles()
// already returns (sizing, focus ring, validation borders) instead of
// replacing it. Light mode returns the original styles untouched.
const withDarkSelectStyles = (base = {}, isDark = false, validated = false) => {
  if (!isDark) return base;

  const layer = (key, extra) => (provided, state) => {
    const current =
      typeof base[key] === "function" ? base[key](provided, state) : provided;
    return { ...current, ...extra(state, current) };
  };

  return {
    ...base,
    control: layer("control", (state, current) => ({
      backgroundColor: state.isDisabled
        ? "var(--dm-bg)"
        : "var(--dm-surface-2)",
      color: "var(--dm-text)",
      // Only the neutral light-gray border turns dark. The focus border and
      // the red/green validation border come from selectCustomStyles().
      ...(!validated && !state.isFocused
        ? {
            borderColor: "var(--dm-border)",
            "&:hover": {
              ...current["&:hover"],
              borderColor: "var(--dm-text-muted)",
            },
          }
        : {}),
    })),
    menu: layer("menu", () => ({
      backgroundColor: "var(--dm-surface-2)",
      boxShadow: "0 0 0 1px var(--dm-border), 0 4px 11px rgba(0, 0, 0, 0.5)",
    })),
    option: layer("option", (state) => ({
      backgroundColor: state.isSelected
        ? "var(--dm-accent-bg)"
        : state.isFocused
          ? "rgba(255, 255, 255, 0.08)"
          : "transparent",
      color: state.isDisabled
        ? "var(--dm-text-muted)"
        : state.isSelected
          ? "var(--dm-accent)"
          : "var(--dm-text)",
      ":active": {
        backgroundColor: state.isSelected
          ? "var(--dm-accent-bg)"
          : "rgba(255, 255, 255, 0.12)",
      },
    })),
    singleValue: layer("singleValue", (state) => ({
      color: state.isDisabled ? "var(--dm-text-muted)" : "var(--dm-text)",
    })),
    input: layer("input", () => ({ color: "var(--dm-text)" })),
    placeholder: layer("placeholder", () => ({
      color: "var(--dm-text-muted)",
    })),
    indicatorSeparator: layer("indicatorSeparator", () => ({
      backgroundColor: "var(--dm-border)",
    })),
    dropdownIndicator: layer("dropdownIndicator", () => ({
      color: "var(--dm-text-muted)",
      ":hover": { color: "var(--dm-text)" },
    })),
    clearIndicator: layer("clearIndicator", () => ({
      color: "var(--dm-text-muted)",
      ":hover": { color: "var(--dm-text)" },
    })),
    noOptionsMessage: layer("noOptionsMessage", () => ({
      color: "var(--dm-text-muted)",
    })),
  };
};

const BulkCollection = () => {
  const { module } = useParams();
  const isLocal = module === "local";
  const pageTitle = isLocal
    ? "CREATE LOCAL RECEIVABLE"
    : "CREATE OVERSEAS RECEIVABLE";
  const userLoggedID = useDecodeToken();
  const [accessType, setAccessType] = useState("");
  const { postedCutoffValidation } = usePostedCutoffValidation();
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const [cutOffData, setCutOffData] = useState([]);
  const [currencyName, setCurrencyName] = useState("");
  const [editableRow, setEditableRow] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [validated, setValidated] = useState(false);
  const [subject1, setSubject1] = useState("");
  const [subject2, setSubject2] = useState("");
  const [subject3, setSubject3] = useState("");
  const [subject2DataList, setSubject2DataList] = useState([]);
  const [subject3DataList, setSubject3DataList] = useState([]);
  const [isSubject1Disabled, setIsSubject1Disabled] = useState(true);
  const [isSubject2Disabled, setIsSubject2Disabled] = useState(true);
  const [isSubject3Disabled, setIsSubject3Disabled] = useState(true);
  const [localCustomerData, setLocalCustomerData] = useState([]);
  const [isCustomerDisabled, setIsCustomerDisabled] = useState(true);
  const [isTransactionDisabled, setIsTransactionDisabled] = useState(true);
  const [currency_db, setCurrency_db] = useState([]);
  const [transactionNumber, setTransactionNumber] = useState("");
  const [issueCheckDate, setIssueCheckDate] = useState("");
  const [processDate, setProcessDate] = useState("");
  // const [collectionDate, setCollectionDate] = useState("");
  const [selected_currency_id, setSelected_currency_id] = useState("");
  const [selectedPayment, setSelectedPayment] = useState("Bank");
  // const [onlinePaymentCheckbox, setOnlinePaymentCheckbox] = useState(false);
  // const [checkCheque, setCheckCheque] = useState(false);
  const [checkOrNone, setCheckOrNone] = useState("none");
  // const [payment_category, setPayment_category] = useState("payment");
  const [bankAmount, setBankAmount] = useState(0);
  const [checkNumber, setCheckNumber] = useState("");
  const [refNumber, setRefNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [amount, setAmount] = useState("");
  // const [totalPayment, setTotalPayment] = useState(0);
  const [floatPayment, setFloatPayment] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [totalAmountSum, setTotalAmountSum] = useState(0);
  const [items, setItems] = useState([
    // {
    //   transactionId: "",
    //   remarks: "",
    //   invoiceDate: "",
    //   dueDate: "",
    //   totalAmount: "",
    //   discount: "",
    //   discountType: "",
    // },
  ]);
  const [editIndex, setEditIndex] = useState(null);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [selectedRow, setSelectedRow] = useState([]);
  const [selectedRowTransactions, setSelectedRowTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const [paginationUrl, setPaginationUrl] = useState("");
  const [hasMounted, setHasMounted] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(1);
  const [isVisibleLiabNote, setIsVisibleLiabNote] = useState(false);
  const [overAllDiscount, setOverallDiscount] = useState({
    type: "amount",
    value: 0,
    convertedAmount: 0,
  });

  const [activeTab, setActiveTab] = useState(() => {
    "Payment List";
  });

  const submitModal = () => {
    const mergedData = [
      ...new Set([...selectedRow, ...selectedRowTransactions]),
    ];

    fetchSalesInvoiceOrderList(
      selectedCustomerId?.value,
      selected_currency_id,
      mergedData,
    );

    setSelectedRowTransactions(mergedData);

    // setSelectedRow([]);
    setSearchTerm("");
    setFilterColumn("all");
    pagination.setCurrentPage(1);
    handleClose();
  };

  const handleIndividualCheckBoxChange = (row) => {
    const transaction_id = row.transaction_id;

    // Linked invoice: an original invoice and the invoice created when it was
    // re-opened reference each other via sales_invoice_id / reopen_sales_invoice_id.
    // Only rows currently loaded in this modal page can be matched here.
    const pairedRow = transactions.find(
      (item) =>
        item.transaction_id !== transaction_id &&
        ((row.reopen_sales_invoice_id &&
          item.sales_invoice_id === row.reopen_sales_invoice_id) ||
          (row.sales_invoice_id &&
            item.reopen_sales_invoice_id === row.sales_invoice_id)),
    );

    if (selectedRow.includes(transaction_id)) {
      setSelectedRow((prev) =>
        prev.filter(
          (item) =>
            item !== transaction_id &&
            (!pairedRow || item !== pairedRow.transaction_id),
        ),
      );
    } else {
      setSelectedRow((prev) => {
        const updated = [...prev, transaction_id];
        if (pairedRow && !updated.includes(pairedRow.transaction_id)) {
          updated.push(pairedRow.transaction_id);
        }
        return updated;
      });
    }
  };

  const handleAllCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked === true) {
      setSelectedRow(transactions.map((item) => item.transaction_id));
    } else {
      setSelectedRow([]);
    }
  };

  const columns = [
    {
      name: (
        <input type="checkbox" onChange={(e) => handleAllCheckboxChange(e)} />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedRow.includes(row.transaction_id)}
          onChange={() => handleIndividualCheckBoxChange(row)}
        />
      ),
      width: "120px",
    },
    {
      name: "Transaction ID",
      selector: (row) => (
        <>
          <span className="d-none">{row.transaction_id || "---"}</span>
          <span>{row.client_transaction_id || "---"}</span>
        </>
      ),
    },
    // {
    //   name: "Remarks",
    //   selector: (row) => row.remarks || "--",
    // },
    {
      name: "Invoice Date",
      selector: (row) => format(row.invoice_date, "MMM/dd/yyyy") || "---",
    },
    {
      name: "Due Date",
      selector: (row) =>
        row.due_date ? format(row.due_date, "MMM/dd/yyyy") : "---",
    },
    {
      name: "DR Number / Container Number",
      selector: (row) => row.dr_number || row.container_number || "---",
      width: "18rem",
    },
    {
      name: "PO Number",
      selector: (row) => row.po_number || "---",
    },
    {
      name: "Amount",
      selector: (row) => truncateToTwoDecimals(row.total_amount) || "---",
    },
    // {
    //   name: "Discount",
    //   selector: (row) =>
    //     truncateToTwoDecimals(row.item_discount + row.transaction_discount),
    // },
  ];

  // Function to add a new row
  const addNewItem = () => {
    if (checkEditPayment()) return;

    if (!selected_currency_id || !selectedCustomerId) {
      swal({
        icon: "error",
        title: "Missing Field",
        text: "Please select currency and customer first",
      });
      return;
    }

    fetchSalesInvoiceOrderList(
      selectedCustomerId?.value,
      selected_currency_id,
      selectedRowTransactions,
    );

    handleShow();
  };

  // Function to delete a row
  const deleteItem = (item, index) => {
    setTransactions((prev) => {
      return [...prev, item];
    });

    setItems((prev) => {
      return prev.filter((_, i) => index !== i);
    });

    setSelectedRow((prev) => {
      return prev.filter((itemRow) => itemRow !== item.transaction_id);
    });

    setSelectedRowTransactions((prev) => {
      return prev.filter((itemRow) => itemRow !== item.transaction_id);
    });
  };

  // Function to toggle edit mode
  // const toggleEdit = (index) => {
  //   setEditIndex(editIndex === index ? null : index);
  // };

  const fetchTransactionCode = () => {
    // Routes Combined
    // Point to the specific transaction endpoints you set up in your backend
    // const endpoint = isLocal
    //   ? "/bulkcollection/transactionBulkCollection"
    //   : "/overseas_bulkcollection/transactionBulkCollection";

    axios
      .get(`${BASE_URL}/bulkcollection/transactionBulkCollection`)
      .then((res) => {
        setTransactionNumber(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchCurrency = () => {
    axios
      .get(BASE_URL + "/currency/sales-currency", {
        params: { destination: isLocal ? "local" : "Overseas" },
      })
      .then((response) => {
        setCurrency_db(response.data);
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  };

  const fetchCutOff = () => {
    axios
      .get(BASE_URL + "/bulkcollection/getCutoffPosted")
      .then((res) => {
        setCutOffData(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Access type (Management/Employee) of the logged in user, used to decide
  // whether the account balance is shown or hidden on this screen.
  const fetchAccessType = () => {
    axios
      .get(`${BASE_URL}/userRole/getUserAccessType/${userLoggedID}`)
      .then((res) => {
        setAccessType(res.data.accessType);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleProcessDate = async (date) => {
    if (checkEditPayment()) return;

    // Field was emptied (typing/backspace): nothing to validate. Process Date is
    // required, so Save stays blocked until a date is entered again (in Cash
    // mode the Cash effect below falls back to today).
    if (!date) {
      setProcessDate("");
      return;
    }

    if (issueCheckDate && toDateOnly(date) > toDateOnly(issueCheckDate)) {
      warnProcessDateAfterMaturity();
      return;
    }

    const previousDate = processDate;

    const isPosted = await postedCutoffValidation(
      date,
      (val) => {
        setProcessDate(val === "" ? previousDate : val);
      },
      "Process Date",
    );

    if (isPosted) {
      return;
    }

    setProcessDate(date);
    warnIfNoCutoff(date, "Process Date");
  };

  const handleIssueCheckDate = async (date) => {
    if (checkEditPayment()) return;

    // Field was emptied (typing/backspace): clear the value. The field is
    // required, so adding a payment stays blocked until a date is entered.
    if (!date) {
      setIssueCheckDate("");
      return;
    }

    if (processDate && toDateOnly(date) < toDateOnly(processDate)) {
      warnMaturityBeforeProcessDate();
      return;
    }

    const previousDate = issueCheckDate;

    const isPosted = await postedCutoffValidation(
      date,
      (val) => {
        setIssueCheckDate(val === "" ? previousDate : val);
      },
      "Issue Check Date",
    );

    if (isPosted) {
      return;
    }

    setIssueCheckDate(date);
    warnIfNoCutoff(date, "Issued Cheque Date");
  };

  // Typed Process Date (MM/dd/yyyy). react-datepicker only calls onChange for a
  // valid date inside minDate/maxDate, so a complete date typed past the
  // Maturity Date would be dropped silently and the text would snap back on
  // blur. Show the same warning handleProcessDate gives for picked dates.
  const handleProcessDateRaw = (e) => {
    // Same guard as handleProcessDate: no changes while a payment row is edited.
    if (checkEditPayment()) {
      e.preventDefault();
      return;
    }

    const next = maskTypedDate(e);
    if (next === null) return;

    const typed = parseCompleteTypedDate(next);
    if (typed && issueCheckDate && typed > toDateOnly(issueCheckDate)) {
      warnProcessDateAfterMaturity();
    }
  };

  // Typed Issued Cheque Date (Maturity Date): same idea against the Process Date.
  const handleIssueCheckDateRaw = (e) => {
    if (checkEditPayment()) {
      e.preventDefault();
      return;
    }

    const next = maskTypedDate(e);
    if (next === null) return;

    const typed = parseCompleteTypedDate(next);
    if (typed && processDate && typed < toDateOnly(processDate)) {
      warnMaturityBeforeProcessDate();
    }
  };

  const dateToday = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const year = today.getFullYear();

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (selectedPayment === "Cash") {
      if (processDate) {
        // Sync maturity date with the chosen process date
        setIssueCheckDate(processDate);
      } else {
        // Fallback to today
        const currentDate = dateToday();
        setIssueCheckDate(currentDate);
        setProcessDate(currentDate);
      }
    }
  }, [selectedPayment, processDate]);

  useEffect(() => {
    setProcessDate(dateToday());
  }, []);

  const handleSubject1Change = (event) => {
    if (checkEditPayment()) return;

    const selectedSubject1 = event.target.value;
    let account_selected = "";

    if (selectedSubject1 === "Account-List") {
      setIsVisibleLiabNote(false);
      account_selected = "Account-List";
    } else if (selectedSubject1 === "Asset Account") {
      setIsVisibleLiabNote(false);
      account_selected = "Asset Account";
    } else if (selectedSubject1 === "Liabilities Account") {
      setIsVisibleLiabNote(true);
      account_selected = "Liabilities Account";
    } else if (selectedSubject1 === "Owner's Equity Account") {
      setIsVisibleLiabNote(false);
      account_selected = "Owner's Equity Account";
    }

    const clearSubjects = () => {
      setSubject1("");
      setSubject2("");
      setSubject3("");
      setBankAmount(0);
      setAccountName("");
      setIsSubject2Disabled(true);
      setIsSubject3Disabled(true);
      setAmount("");
    };

    try {
      axios
        .get(`${BASE_URL}/bulkcollection/getSubject1LocalCollection`, {
          params: {
            account_selected: account_selected,
            selectedPayment,
            selected_currency_id,
          },
        })
        .then((res) => {
          setSubject1(selectedSubject1);
          setSubject2("");
          setSubject3("");
          setAmount("");
          setBankAmount(0);
          setAccountName("");
          setIsSubject2Disabled(false); // Enable Subject 2 after Subject 1 selection
          setIsSubject3Disabled(true); // Reset and disable Subject 3

          setSubject2DataList(res.data); //retrieve subject 2 data
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            swal({
              icon: "warning",
              title: "Warning",
              text: error.response.data.message,
            }).then(() => clearSubjects());
            return;
          }
        });
    } catch (error) {
      console.log(error);
      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
      });
    }
  };

  const handleSubject2Change = (selectedOption) => {
    if (checkEditPayment()) return;

    const selectedSubject2 = selectedOption;

    try {
      axios
        .get(`${BASE_URL}/bulkcollection/getSubject3LocalCollection`, {
          params: {
            subjectId: selectedSubject2?.value,
            selected_currency_id,
          },
        })
        .then((res) => {
          setSubject3DataList(res.data);
          setSubject2(selectedSubject2);
          setSubject3("");
          setBankAmount(0);
          setAccountName("");
          setIsSubject3Disabled(false); // Enable Subject 3 after Subject 2 selection
        });
    } catch (error) {
      console.log(error);
      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
      });
    }
  };

  const handlePaymentMethod = (e) => {
    if (checkEditPayment()) return;

    const paymentMethod = e.target.value;
    setSelectedPayment(paymentMethod);
    setCheckOrNone("none");
    setSubject1("");
    setSubject2("");
    setSubject3("");
    setRefNumber("");
    setCheckNumber("");
    setBankAmount(0);
    setAccountName("");
    setIsSubject2Disabled(true);
    setIsSubject3Disabled(true);

    // if (paymentMethod) {
    //   axios
    //     .get(`${BASE_URL}/bulkcollection/getAccountList/${paymentMethod}`)
    //     .then((res) => {
    //       setAccountListData(res.data);
    //       setValidated(false);

    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     });
    // } else {
    //   setAccountListData([]);
    // }
  };

  // const handleOnlineCheck = (e) => {
  // const isChecked = e.target.checked;
  // setOnlinePaymentCheckbox(isChecked);
  // if (isChecked) {
  //   setCheckCheque(false);
  // }
  // };

  //   const handleCheque = (e) => {
  //   const isChecked = e.target.checked;
  //   setCheckCheque(isChecked);
  //   if (isChecked) {
  //     setOnlinePaymentCheckbox(false);
  //   }
  // };

  const handleAccountChange = (selectedOption) => {
    if (checkEditPayment()) return;

    const selectedAccountId = selectedOption?.value;
    setSubject3(selectedOption);

    // Find the selected account in the accountListData
    const selectedAccount = subject3DataList.find(
      (account) => String(account.id) === String(selectedAccountId),
    );

    if (selectedAccount) {
      // For Liability Validation if the selected liability account has zero balance
      if (
        subject1 === "Liabilities Account" &&
        parseFloat(selectedAccount.amount || 0) <= 0
      ) {
        swal({
          icon: "warning",
          title: "Zero Liability Acount Balance",
          text: `This liability account ${selectedAccount.account_name} has a balance of zero and cannot be selected.`,
          button: "OK",
        });
        setSubject3("");
        setBankAmount(0);
        setAccountName("");
        return;
      }

      setSubject3(selectedOption);
      setBankAmount(selectedAccount.amount);
      setAccountName(selectedAccount.account_name);

      // Same pattern as the Cheque handler above: auto-fill Amount with
      // the current Receivable Balance.
      setAmount(balance_now.toFixed(2).toString());
    }
  };

  // useEffect(() => {
  //   // Fetch initial customer data
  //   axios
  //     .get(BASE_URL + "/bulkcollection/getCustomerLocalSales")
  //     .then((res) => {
  //       const uniqueCustomers = [];
  //       const customerSet = new Set();

  //       res.data.forEach((invoice) => {
  //         if (!customerSet.has(invoice.customer.customer_id)) {
  //           customerSet.add(invoice.customer.customer_id);
  //           uniqueCustomers.push(invoice.customer);
  //         }
  //         console.log(invoice);
  //       });

  //       setLocalCustomerData(uniqueCustomers);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, []);

  const handleCurrencyChange = (value) => {
    if (checkEditPayment()) return;
    setSelected_currency_id(value);
    const currency_symbol = currency_db.find(
      (currency) => String(currency.id) === String(value),
    );

    if (currency_symbol) {
      setCurrencyName(currency_symbol.currency_name);
      setCurrencyRate(currency_symbol.currency_rate);
    }

    //BOTH IN SAME BULK COLLECTION
    const END_POINT = isLocal
      ? "/bulkcollection/getCustomerLocalSales"
      : "/bulkcollection/getCustomerOverseasSales";

    axios
      .get(`${BASE_URL}${END_POINT}`, {
        params: {
          currency_id: value,
          destination: isLocal ? "local" : "Overseas",
        },
      })
      .then((res) => {
        const uniqueCustomers = [];
        const customerSet = new Set();

        res.data.forEach((invoice) => {
          if (!customerSet.has(invoice.customer.customer_id)) {
            customerSet.add(invoice.customer.customer_id);
            uniqueCustomers.push(invoice.customer);
          }
          console.log(invoice);
        });

        setLocalCustomerData(uniqueCustomers);
        setIsCustomerDisabled(false);

        if (uniqueCustomers.length === 0) {
          swal({
            icon: "warning",
            title: "No Customer Found",
            text: "There's no sales invoice found under this currency",
          });
          return;
        }
      })
      .catch((err) => {
        console.log(err);
      });
    setSelectedCustomerId("");
    setTransactions([]);
    setItems([]);
  };

  const handleCustomerChange = (selectedOption) => {
    if (checkEditPayment()) return;
    const customerId = selectedOption;

    const hasItems = items.length > 0;
    const hasPayments = floatPayment.length > 0;

    const proceedWithCustomerChange = () => {
      setItems([]); // clear invoice rows
      setFloatPayment([]); // clear payment list
      // setTotalPayment(0); // reset payment total
      setSelectedRowTransactions([]); // clear checked transaction IDs
      setSelectedRow([]); // clear modal checkbox selections
      setOverallDiscount({ type: "amount", value: 0, convertedAmount: 0 }); // reset discount
      setSelectedCustomerId(customerId);

      if (customerId) {
        fetchSalesInvoiceOrderList(
          customerId?.value,
          selected_currency_id,
          [], // pass empty array instead of stale selectedRowTransactions
        );
      }
    };

    if (hasItems || hasPayments) {
      swal({
        title: "Change Customer?",
        text: "Changing the customer will clear all added invoice rows and payment entries. Do you want to continue?",
        icon: "warning",
        buttons: ["Cancel", "Continue"],
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          proceedWithCustomerChange();
        }
      });
    } else {
      proceedWithCustomerChange();
    }
  };

  const pagination = useServerPagination(paginationUrl, 10);

  // const paginationHook = usePagination(transactions, 10);

  const fetchSalesInvoiceOrderList = (
    customerId,
    selected_currency_id,
    selectedRowTransactions,
  ) => {
    // setPaginationUrl(
    //   `${BASE_URL}/bulkcollection/getTransactionsByCustomer/${customerId}/${selected_currency_id}`
    // );
    // const baseRoute = isLocal ? "/bulkcollection" : "/overseas_bulkcollection";

    pagination.updateApiUrl(
      `${BASE_URL}/bulkcollection/getTransactionsByCustomer/${customerId}/${selected_currency_id}`,
    );
    pagination.updateParams({
      searchText: searchTerm,
      filterColumn,
      selectedRow: selectedRowTransactions,
      destination: isLocal ? "local" : "Overseas",
    });
    setIsTransactionDisabled(false);
  };

  useEffect(() => {
    let grossAmount = 0;
    let totalDiscount = 0;

    items.forEach((item) => {
      const amount = parseFloat(item.total_amount) || 0;
      grossAmount += amount;

      let itemDiscount = 0;
      const discVal = parseFloat(item.collectionDiscountValue) || 0;

      if (item.collectionDiscountType === "percent") {
        itemDiscount = (discVal / 100) * amount;
      } else {
        itemDiscount = discVal;
      }

      // Plus adds to the overall discount (+), minus deducts from overall discount (-)
      if (item.collectionDiscountOperator === "-") {
        totalDiscount -= itemDiscount;
      } else {
        totalDiscount += itemDiscount;
      }
    });

    setTotalAmountSum(grossAmount);
    setOverallDiscount({
      type: "amount",
      value: totalDiscount,
      convertedAmount: totalDiscount,
    });
  }, [items]);

  const activeFloatPayment = floatPayment.map((payment, i) =>
    editableRow === i ? { ...payment, ...editedData } : payment,
  );

  const totalCashPaid = activeFloatPayment.reduce(
    (acc, data) =>
      data.paymentMethod === "Cash"
        ? acc + parseFloat(String(data.amount).replace(/,/g, "") || 0)
        : acc,
    0,
  );

  const totalBankPaid = activeFloatPayment.reduce(
    (acc, data) =>
      data.checkNumber === "" &&
      data.refNumber === "" &&
      data.paymentMethod === "Bank"
        ? acc + parseFloat(String(data.amount).replace(/,/g, "") || 0)
        : acc,
    0,
  );

  const totalCheckPaid = activeFloatPayment.reduce(
    (acc, data) =>
      data.checkNumber !== ""
        ? acc + parseFloat(String(data.amount).replace(/,/g, "") || 0)
        : acc,
    0,
  );

  // const totalOnlinePaid = activeFloatPayment.reduce(
  //   (acc, data) =>
  //     data.refNumber !== "" && data.paymentMethod === "Bank"
  //       ? acc + parseFloat(String(data.amount).replace(/,/g, "") || 0)
  //       : acc,
  //   0,
  // );

  const totalPayment = activeFloatPayment.reduce(
    (acc, data) => acc + parseFloat(String(data.amount).replace(/,/g, "") || 0),
    0,
  );

  const toPayAmount =
    parseFloat(totalAmountSum) - parseNumber(overAllDiscount.convertedAmount);

  const advance_payment =
    totalPayment - totalAmountSum <= 0 ? 0 : totalPayment - totalAmountSum;
  const balance_now = advance_payment + (toPayAmount - totalPayment);

  const handleAmountValue = (val) => {
    if (val === "" || val === ".") {
      setAmount(val);
      return;
    }

    const numericValue = parseFloat(val);
    const isLiability = subject1 === "Liabilities Account";
    const liabilityLimit = parseFloat(bankAmount || 0);

    if (
      isLiability &&
      numericValue > liabilityLimit &&
      liabilityLimit <= balance_now
    ) {
      swal({
        icon: "warning",
        title: "Amount Adjusted",
        text:
          accessType === "Management"
            ? `Please input not greater than the account balance. The amount adjusted to match your remaining balance (${truncateToTwoDecimals(liabilityLimit)}).`
            : `Please input not greater than the account balance. The amount has been adjusted to match the available balance.`,
        ///`Please input not greater than selected liability account balance. The amount adjusted to match liability remaining balance (${truncateToTwoDecimals(liabilityLimit)})`,
        buttons: false,
        timer: 2000,
      });
      return;
    }

    // commented kasi ang lalagpas sa balance now is magiging advance payment
    // if (numericValue > balance_now) {
    //   swal({
    //     icon: "warning",
    //     title: "Amount Adjusted",
    //     text: `Please input not greater than the balance. The amount adjusted to match your remaining balance (${truncateToTwoDecimals(balance_now)}).`,
    //     buttons: false,
    //     timer: 2000,
    //   });
    //   setAmount(balance_now.toFixed(2).toString());
    //   return;
    // }

    setAmount(val);
  };

  const handleEditAmountValue = (val) => {
    if (val === "" || val === ".") {
      setEditedData((prev) => ({ ...prev, amount: val }));
      return;
    }

    const numericValue = parseFloat(val);

    const sumOfOtherPayments = floatPayment.reduce((acc, data, i) => {
      if (i === editableRow) return acc;
      return acc + parseFloat(String(data.amount).replace(/,/g, "") || 0);
    }, 0);

    const maxAllowed = toPayAmount - sumOfOtherPayments;

    if (numericValue > maxAllowed) {
      swal({
        icon: "warning",
        title: "Amount Adjusted",
        text:
          accessType === "Management"
            ? `Please input not greater than the account balance. The amount adjusted to match your remaining balance (${truncateToTwoDecimals(maxAllowed)}).`
            : `Please input not greater than the account balance. The amount has been adjusted to match the available balance.`,
        //`Please input not greater than the balance. The amount adjusted to match your remaining balance (${truncateToTwoDecimals(maxAllowed)}).`,
        buttons: false,
        timer: 2000,
      });
      setEditedData((prev) => ({
        ...prev,
        amount: maxAllowed.toFixed(2).toString(),
      }));
      return;
    }

    setEditedData((prev) => ({ ...prev, amount: val }));
  };

  const handleEditIssuedDate = async (date, index) => {
    if (editableRow !== index) return;

    const previousDate = editedData.issuedDate;

    const safeSetter = (val) => {
      setEditedData((prev) => ({
        ...prev,
        issuedDate: val === "" ? previousDate : val,
      }));
    };

    const isPosted = await postedCutoffValidation(
      date,
      safeSetter,
      "Issued Check Date",
    );
    if (isPosted) return;

    setEditedData((prev) => ({
      ...prev,
      issuedDate: date,
    }));
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();

    if (checkEditPayment()) return;

    if (!selectedPayment) {
      swal({
        icon: "error",
        title: "Payment Method Required",
        text: "Please select either Bank or Cash as the payment method.",
        buttons: false,
        timer: 2000,
      });
      return;
    }

    const formatAmount = parseFloat(amount);

    if (formatAmount <= 0) {
      swal({
        icon: "error",
        title: "Amount Required",
        text: "Please ensure the amount is greater than zero before submitting.",
        buttons: false,
        timer: 2000,
      });

      return;
    }

    const form = e.currentTarget;
    setIsVisibleLiabNote(false);

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill the red text fields",
        buttons: false,
        timer: 2000,
      });
    } else {
      if (
        selectedPayment === "Bank" &&
        checkNumber &&
        !(await validateCheckNumbers(checkNumber, {
          pending: floatPayment
            .filter((payment) => !payment.isDeleted)
            .map((payment) => payment.checkNumber),
        }))
      )
        return;

      swal({
        title: "Add this payment?",
        text: "",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          const baseNewPayment = {
            paymentMethod: selectedPayment,
            subject1: subject1,
            subject2: subject2?.value,
            subject3: subject3?.value,
            accountName: accountName,
            amount: formatAmount,
            issuedDate: issueCheckDate,
            processDate: processDate,
            checkNumber: checkNumber,
            refNumber: refNumber,
            checkOrNone: checkOrNone || null,
            status: "Pending",
          };

          // setFloatPayment((prevPayments) => {
          //   const newIndex = prevPayments.length + 1;

          //   const newPayment = {
          //     ...baseNewPayment,
          //     transactionNumber: `${transactionNumber}_p${newIndex}`,
          //   };

          //   // // Check for matching payment based on all the conditions
          //   // const existingPaymentIndex = prevPayments.findIndex(
          //   //   (payment) =>
          //   //     payment.subject3 === newPayment.subject3 &&
          //   //     payment.issuedDate === newPayment.issuedDate &&
          //   //     // First condition: match by paymentMethod, checkNumber, and issuedDate
          //   //     ((!newPayment.refNumber &&
          //   //       payment.paymentMethod === newPayment.paymentMethod &&
          //   //       payment.checkNumber === newPayment.checkNumber) ||
          //   //       // Second condition: match when both are "Cash" with the same issuedDate
          //   //       (payment.paymentMethod === "Cash" &&
          //   //         newPayment.paymentMethod === "Cash") ||
          //   //       // Third condition: match when both are "Bank" with empty checkNumber, empty refNumber, and same issuedDate
          //   //       (payment.paymentMethod === "Bank" &&
          //   //         newPayment.paymentMethod === "Bank" &&
          //   //         !payment.checkNumber &&
          //   //         !payment.refNumber &&
          //   //         !newPayment.checkNumber &&
          //   //         !newPayment.refNumber) ||
          //   //       // Fourth condition: match when both are "Bank" with the same refNumber and issuedDate
          //   //       (newPayment.refNumber &&
          //   //         payment.paymentMethod === "Bank" &&
          //   //         newPayment.paymentMethod === "Bank" &&
          //   //         payment.refNumber === newPayment.refNumber)),
          //   // );

          //   // if (existingPaymentIndex !== -1) {
          //   //   const updatedPayments = [...prevPayments];
          //   //   updatedPayments[existingPaymentIndex] = {
          //   //     ...updatedPayments[existingPaymentIndex],
          //   //     amount:
          //   //       updatedPayments[existingPaymentIndex].amount +
          //   //       newPayment.amount,
          //   //   };
          //   //   return updatedPayments;
          //   // }

          //   return [...prevPayments, newPayment];
          // });

          setFloatPayment((prevPayments) => {
            const newIndex = prevPayments.length + 1;

            // Total amount already applied to the receivable
            const totalApplied = prevPayments.reduce(
              (sum, payment) => sum + (payment.appliedAmount ?? payment.amount),
              0,
            );

            const remainingReceivable = Math.max(
              0,
              totalAmountSum - totalApplied,
            );

            // How much of this payment can still be applied
            const appliedAmount = Math.min(formatAmount, remainingReceivable);

            // Excess becomes advance payment
            const advanceAmount = Math.max(
              0,
              formatAmount - remainingReceivable,
            );

            const newPayment = {
              ...baseNewPayment,
              transactionNumber: `${transactionNumber}_p${newIndex}`,

              // Customer's actual payment
              // amount: formatAmount,

              // Applied to the invoice
              appliedAmount,

              // Excess payment
              advanceAmount,

              // Optional flag
              isAdvancePayment: advanceAmount > 0,
            };

            return [...prevPayments, newPayment];
          });

          swal({
            icon: "success",
            title: "Payment Added",
            text: "The new payment has been added successfully",
            buttons: false,
            timer: 2000,
          }).then(() => {
            setValidated(false);
            setSelectedPayment("Bank");
            setAccountName("");
            setAmount("");
            setCheckNumber("");
            setRefNumber("");
            setIssueCheckDate("");
            setSubject1("");
            setSubject2("");
            setSubject3("");
            setIsSubject2Disabled(true);
            setIsSubject3Disabled(true);
            setCheckOrNone("none");
          });
        }
      });
    }
    setValidated(true);
  };

  console.log(floatPayment);

  const handleSavePayment = async () => {
    if (checkEditPayment()) return;

    if (!items.length) {
      swal({
        icon: "warning",
        title: "No Items Added",
        text: "Please add at least one item to your order list before proceeding.",
      });
      return;
    }

    if (!processDate) {
      swal({
        title: "Required Field Missing",
        text: "Please ensure Process Date is filled out.",
        icon: "error",
        buttons: false,
        timer: 3000,
      });
      return;
    }

    if (balance_now < 0) {
      swal({
        icon: "error",
        title: "Oopps!",
        text: "New balance cannot be less than zero. Please check your payment.",
      });
      return;
    }

    const confirmed = await swal({
      title: "Create this new collection?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (confirmed) {
      try {
        // Routes Combined
        // const saveEndpoint = isLocal
        //   ? "/bulkcollection/addPaymentLocal"
        //   : "/overseas_bulkcollection/";

        const type = isLocal ? "Local" : "Overseas";
        const module_from = isLocal
          ? "Local-Bulk-Collection"
          : "Overseas-Bulk-Collection";

        const res = await axios.post(
          `${BASE_URL}/bulkcollection/addPaymentLocal`,
          {
            items,
            selectedCustomerId: selectedCustomerId?.value,
            processDate,
            // selectedPayment,
            // bankAmount,
            // checkNumber,
            // refNumber,
            // amount,
            // accountName,
            // date,
            floatPayment,
            balance_now,
            transactionNumber,
            selected_currency_id,
            module_from: module_from,
            currencyRate,
            type: type,
            totalAmount: totalAmountSum,
            userLoggedID,
            discount_type: overAllDiscount.type,
            discount_value: overAllDiscount.value,
            discount_converted: parseNumber(overAllDiscount.convertedAmount),
          },
        );

        if (res.status === 200) {
          const wrapper = document.createElement("div");
          wrapper.classList.add("center-swal-text", "pb-4");
          wrapper.innerHTML =
            "New Collection added to this bulk collection transaction";
          swal({
            title: "Success",
            content: wrapper,
            icon: "success",
            buttons: false,
            timer: 2000,
            dangerMode: true,
          }).then(() => {
            navigate(
              isLocal ? "/sales/local-collections" : "/sales/local-collections",
            );
          });
        }
      } catch (error) {
        console.error("Error saving payment:", error);
        swal({
          title: "Something went wrong",
          text: "Please contact your support immediately",
          icon: "error", // Changed to error for better clarity
          buttons: false,
          timer: 2000,
          dangerMode: true,
        });
      }
    }
    // setValidated(true);
  };

  const handleEditClick = (index, data) => {
    if (checkEditPayment()) return;
    setEditableRow(index);
    setEditedData({ ...data });
  };

  const handleSaveEdit = async (index) => {
    if (editableRow === index) {
      const originalCheck = floatPayment[index]?.checkNumber || "";
      if (
        editedData.checkNumber &&
        editedData.checkNumber.trim() !== originalCheck.trim() &&
        !(await validateCheckNumbers(editedData.checkNumber, {
          pending: floatPayment
            .filter((payment, i) => i !== index && !payment.isDeleted)
            .map((payment) => payment.checkNumber),
        }))
      )
        return;

      setFloatPayment((prevPayments) => {
        const updatedPayments = prevPayments.map((payment, i) =>
          i === index ? { ...payment, ...editedData } : payment,
        );

        return updatedPayments;
      });
      setEditableRow(null);
    }
  };

  const checkEditPayment = () => {
    if (editableRow !== null) {
      swal({
        icon: "warning",
        title: "Payment Edit in Progress",
        text: "Please click (✔) or (✖) buttons of your current payment edit before performing other actions.",
      });
      return true;
    }
    return false;
  };

  const handleDeletePayment = (index) => {
    if (checkEditPayment()) return;
    setFloatPayment((prevPayments) => {
      const filteredPayments = prevPayments.filter((_, i) => i !== index);

      const updatedPayments = filteredPayments.map((payment, newIndex) => {
        const baseTn = payment.transactionNumber
          ? payment.transactionNumber.split("_p")[0]
          : transactionNumber;

        return {
          ...payment,
          transactionNumber: `${baseTn}_p${newIndex + 1}`,
        };
      });

      return updatedPayments;
    });

    // swal({
    //   icon: "success",
    //   title: "Payment Deleted",
    //   text: "The payment has been removed successfully",
    //   buttons: false,
    //   timer: 2000,
    // });
  };

  // Customer Options for dropdown select

  const customerOptions = localCustomerData
    .map((item) => ({
      value: item.customer_id,
      // Company name first, else first name + last name (last name only if available)
      label:
        item.company_name ||
        [item.first_name, item.last_name].filter(Boolean).join(" "),
    }))
    .sort((a, b) => (b.label ?? "").localeCompare(a.label ?? ""));

  // Subject 2 Options for dropdown select
  const subject2Options = subject2DataList
    .map((item) => ({
      value: item.id,
      label: item.subject_name,
      subject_type: item.subject_type,
    }))
    .sort((a, b) => (b.label ?? "").localeCompare(a.label ?? ""));

  // Subject 3 Options for dropdown select
  const subject3Options = subject3DataList
    .map((item) => ({
      value: item.id,
      label: item.account_name,
    }))
    .sort((a, b) => (b.label ?? "").localeCompare(a.label ?? ""));

  // Custom input for DatePicker to Prevent user typing/input
  const CustomInput = React.forwardRef(
    ({ value, onClick, index, generateYears, disabled }, ref) => (
      <input
        type="text"
        className="form-control p-2 w-100"
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          caretColor: "transparent",
        }}
        onClick={(e) => {
          if (disabled) return; // Prevent clicking when disabled
          onClick(e);

          const date = new Date(value).getFullYear();
          if (generateYears) generateYears(date);
        }}
        value={value}
        ref={ref}
        placeholder="Select Date"
        required
        disabled={
          disabled || (index !== undefined ? editableRow !== index : false)
        }
      />
    ),
  );

  useEffect(() => {
    fetchTransactionCode();
    fetchCurrency();
    fetchCutOff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userLoggedID) {
      fetchAccessType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoggedID]);

  useEffect(() => {
    fetchSalesInvoiceOrderList(
      selectedCustomerId?.value,
      selected_currency_id,
      selectedRowTransactions,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm("");
  }, [filterColumn]);

  const paginationTransactions = pagination?.data?.transactions || [];

  useEffect(() => {
    setTransactions(pagination?.data?.transactions);

    if (pagination?.data?.items) {
      // Skip if all selectedRows already exist in items
      const existingIds = items.map((item) => item.transaction_id);
      const isFullySynced =
        selectedRow.every((id) => existingIds.includes(id)) &&
        items.length === selectedRow.length;

      if (isFullySynced) return;

      const newItems = pagination.data.items.filter(
        (item) =>
          selectedRow.includes(item.transaction_id) &&
          !existingIds.includes(item.transaction_id),
      );

      if (newItems.length > 0) {
        setItems((prev) => [
          ...prev,
          ...newItems.map((item) => ({
            ...item,
            collectionDiscountType: "percent",
            collectionDiscountValue: "",
            collectionDiscountOperator: "+",
            collectionDiscountConverted: 0,
          })),
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationTransactions]);

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    if (items.length === 0) {
      swal({
        icon: "warning",
        title: "Warning",
        text: "Please select from the order list first before proceeding to payment method",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayment, checkOrNone]);

  // function to handle currency rate formatting (similar to create_invoice.jsx)
  const handleCurrencyRateChange = (e) => {
    let inputValue = e.target.value.replace(/[^0-9.]/g, "");
    if ((inputValue.match(/\./g) || []).length > 1) return;
    let [integerPart, decimalPart] = inputValue.split(".");
    if (integerPart) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    let formattedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    setCurrencyRate(formattedValue);
  };

  const handleItemDiscountChange = (index, key, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [key]: value,
      };
      return updated;
    });
  };

  const handleTabSelect = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link
              to={
                isLocal
                  ? "/sales/local-collections"
                  : "/sales/local-collections"
              }
              className="text-dark mx-2"
            >
              <i class="fa-solid fa-arrow-left"></i>
            </Link>
            {pageTitle}
          </span>
        </div>
      </div>

      <Container fluid className="mt-3">
        <Row className="p-2">
          <Col sm>
            <span>Transaction Number</span>
            <Form.Control
              type="text"
              name="transactionNumber"
              id="transactionNumber"
              className="p-2"
              value={transactionNumber}
              readOnly
            />
          </Col>
          <Col sm>
            <span>
              Process Date <span className="text-danger">*</span>
            </span>
            <DatePicker
              selected={processDate ? new Date(processDate) : null}
              onChange={handleProcessDate}
              onChangeRaw={handleProcessDateRaw}
              // Month list + year grid header, same as CustomDatePicker.
              // "Today" goes through handleProcessDate, so the Maturity Date
              // and posted cutoff checks still apply.
              renderCustomHeader={(headerProps) => (
                <CustomDatePickerHeader
                  {...headerProps}
                  onToday={handleProcessDate}
                />
              )}
              customInput={<TypedDateInput />}
              className={`form-control p-2${
                validated && !processDate ? " is-invalid" : ""
              }`}
              wrapperClassName="w-100"
              // placeholderText="MM/dd/yy"
              placeholderText={DATE_INPUT_FORMAT}
              dateFormat={DATE_INPUT_FORMAT}
              strictParsing
              autoComplete="off"
              required
              ///disabled={selectedPayment === "Cash"}
              maxDate={
                issueCheckDate
                  ? new Date(new Date(issueCheckDate).setHours(23, 59, 59, 999))
                  : undefined
              }
            />
          </Col>
        </Row>
        <Row className="p-2">
          <Col sm>
            <span>
              Currency <span className="text-danger">*</span>
              {/* {selected_currency_id &&
              selected_currency_id !==
                "11111111-1111-1111-1111-111111111111" ? (
                <span className="text-muted mb-1">
                  {" "}
                  Current Rate: {currencyRate}
                </span>
              ) : null} */}
            </span>
            <InputGroup className="mb-2">
              <Form.Select
                name="currencyId"
                id="currencyId"
                className="p-2"
                required
                onChange={(e) => handleCurrencyChange(e.target.value)}
                value={selected_currency_id}
                onMouseDown={(e) => {
                  if (currency_db.length === 0) {
                    e.preventDefault();
                    const wrapper = document.createElement("div");
                    wrapper.classList.add("center-swal-text");
                    wrapper.innerHTML = `There are no approved transactions in the <strong>Sales Invoice</strong> module that haven't already been added to <strong>${isLocal ? "Local" : "Overseas"} Collection</strong>.`;

                    swal({
                      icon: "warning",
                      title: "Warning",
                      content: wrapper,
                    });
                  }
                }}
              >
                <option value="" selected disabled>
                  Select Currency
                </option>
                {currency_db.map((data) => (
                  <option key={data.id} value={data.id}>
                    {data.currency_name} - {currencyList[data.currency_name]}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Col>
          {selected_currency_id &&
          selected_currency_id !== "11111111-1111-1111-1111-111111111111" ? (
            <Col sm className="d-none">
              <span>
                Currency Rate<span className="text-danger">*</span>
              </span>
              <Form.Control
                type="text"
                name="currencyRate"
                id="currencyRate"
                className="p-2"
                required
                onChange={handleCurrencyRateChange}
                value={currencyRate}
                readOnly
              />
            </Col>
          ) : null}
          <Col sm>
            <span>
              Customer <span className="text-danger">*</span>
            </span>
            <div className="mb-2">
              <Select
                options={customerOptions}
                value={selectedCustomerId}
                onChange={handleCustomerChange}
                placeholder={`Select Customer`}
                styles={withDarkSelectStyles(
                  selectCustomStyles(selectedCustomerId, validated, "0.12rem"),
                  isDark,
                  validated,
                )}
                isDisabled={isCustomerDisabled}
                required
                isSearchable
              />
            </div>
          </Col>
        </Row>
        <Row className="p-2">
          <Col sm>
            <span>Receivable Balance</span>
            <InputGroup className="mb-2">
              <InputGroup.Text>
                {currencySymbol[currencyName] || ""}
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="p-2 pointer-events-none"
                id="totalAmountSum"
                value={truncateToTwoDecimals(totalAmountSum)}
                readOnly
              />
            </InputGroup>
          </Col>
          <Col sm>
            <div className="p-0">
              <span>Overall Collection Discount</span>
              <InputGroup className="mb-2">
                <InputGroup.Text>
                  {currencySymbol[currencyName] || ""}
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  className="p-2 pointer-events-none"
                  id="overallDiscountAmount"
                  value={truncateToTwoDecimals(overAllDiscount.convertedAmount)}
                  readOnly
                />
              </InputGroup>
            </div>
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <div className="w-100 d-flex align-items-center mt-3 p-2">
          <h5>Order List</h5>
          <hr className="flex-grow-1 mx-3" />
        </div>
        <div className="w-100 mt-2 p-2 new-item-custom scrollable-contents">
          <Table bordered hover responsive className="fixed-inputs-table">
            <thead className="table-light">
              <tr>
                <th className="p-2">Transaction ID</th>
                <th className="p-2">Remarks</th>
                <th className="p-2">Invoice Date</th>
                <th className="p-2">Due Date</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Collection Discount</th>
                <th className="p-2">Action</th>
              </tr>
            </thead>
            {items.length > 0 ? (
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="text"
                        className="d-none"
                        readOnly
                        value={item.transaction_id || "---"}
                      />
                      <Form.Control
                        type="text"
                        className="pointer-events-none"
                        readOnly
                        value={item.client_transaction_id || "---"}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        className="pointer-events-none"
                        readOnly
                        value={item.remarks || "---"}
                      />
                    </td>
                    <td>
                      <DatePicker
                        selected={item.invoice_date}
                        dateFormat="MMM/dd/yyyy"
                        className="form-control pointer-events-none"
                        readOnly
                      />
                    </td>
                    <td>
                      <DatePicker
                        selected={item.due_date}
                        dateFormat="MMM/dd/yyyy"
                        className="form-control pointer-events-none"
                        readOnly
                      />
                    </td>
                    <td>
                      <InputGroup>
                        <InputGroup.Text>
                          {currencySymbol[currencyName] || ""}
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="pointer-events-none"
                          value={
                            truncateToTwoDecimals(item.total_amount) || "---"
                          }
                          readOnly
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <InputGroup>
                        <InputGroup.Text
                          id={`discount-type-${index}`}
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (!selectedCustomerId) return;
                            const newType =
                              item.collectionDiscountType === "percent"
                                ? "amount"
                                : "percent";
                            let newValue = item.collectionDiscountValue;

                            if (
                              newType === "percent" &&
                              parseFloat(newValue) >= 100
                            ) {
                              newValue = 100;
                            }

                            setItems((prev) => {
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                collectionDiscountType: newType,
                                collectionDiscountValue: newValue,
                              };
                              return updated;
                            });
                          }}
                        >
                          {item.collectionDiscountType === "percent"
                            ? "%"
                            : currencySymbol[currencyName] || ""}
                        </InputGroup.Text>
                        <NumberInDecimals
                          className="form-control"
                          disabled={!selectedCustomerId || !items.length}
                          value={item.collectionDiscountValue || ""}
                          placeholder="0.00"
                          decimals={2}
                          max={
                            item.collectionDiscountType === "percent"
                              ? 100
                              : parseFloat(item.total_amount)
                          }
                          onChange={(val) =>
                            handleItemDiscountChange(
                              index,
                              "collectionDiscountValue",
                              val,
                            )
                          }
                        />
                        <InputGroup.Text
                          id={`discount-operator-${index}`}
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (!selectedCustomerId) return;
                            const newOp =
                              item.collectionDiscountOperator === "+"
                                ? "-"
                                : "+";
                            handleItemDiscountChange(
                              index,
                              "collectionDiscountOperator",
                              newOp,
                            );
                          }}
                        >
                          {item.collectionDiscountOperator || "+"}
                        </InputGroup.Text>
                      </InputGroup>
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteItem(item, index)}
                      >
                        <i className="fa-solid fa-trash-can"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <caption className="text-center mt-3">
                There are no records to display
              </caption>
            )}
          </Table>
        </div>
        <div className="w-100 d-flex justify-content-end mt-2">
          <Button
            size="sm"
            variant="primary"
            type="button"
            onClick={addNewItem}
          >
            New Item
          </Button>
        </div>
        <Modal
          show={show}
          size="xl"
          onHide={() => {
            setFilterColumn("all");
            setSearchTerm("");
            handleClose();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Order List</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => {
                    let input = e.target.value;

                    // Remove commas
                    const raw = String(input).replace(/,/g, "");

                    const [intPart, decimalPart] = raw.split(".");

                    // Add commas to integer part
                    const withCommas = intPart.replace(
                      /\B(?=(\d{3})+(?!\d))/g,
                      ",",
                    );

                    // Join back decimal part if it exists
                    const formatted =
                      decimalPart !== undefined
                        ? `${withCommas}.${decimalPart}`
                        : withCommas;

                    if (
                      transactions.find((item) => {
                        const price = item.total_amount;
                        const precision17 = Number(
                          price.toPrecision(17),
                        ).toString();
                        const fixed17 = price.toFixed(17);
                        const fixed2 = price.toFixed(2);

                        return (
                          precision17.includes(raw) ||
                          fixed17.includes(raw) ||
                          fixed2.includes(raw)
                        );
                      })
                    ) {
                      setSearchTerm(formatted);
                    } else {
                      setSearchTerm(e.target.value);
                    }
                  }}
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  className="dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fa-solid fa-sliders"></i>
                </Button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className={`dropdown-item ${
                        filterColumn === "all" ? "active" : ""
                      }`}
                      onClick={() => setFilterColumn("all")}
                    >
                      All
                    </button>
                  </li>
                  {[
                    // { value: "transaction_id", label: "Transaction ID" },
                    // { value: "remarks", label: "Remarks" },
                    { value: "invoice_date", label: "Invoice Date" },
                    { value: "due_date", label: "Due Date" },
                    {
                      value: "dr_number",
                      label: "DR Number / Container Number",
                    },
                    {
                      value: "po_number",
                      label: "PO Number",
                    },
                    { value: "total_amount", label: "Amount" },
                    // { value: "discount", label: "Discount" },
                  ].map(({ value, label }) => (
                    <li key={value}>
                      <button
                        className={`dropdown-item ${
                          filterColumn === value ? "active" : ""
                        }`}
                        onClick={() => setFilterColumn(value)}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </InputGroup>
            </Row>
            <DataTable
              columns={columns}
              data={transactions}
              customStyles={customStyles}
              onRowClicked={(row) => handleIndividualCheckBoxChange(row)}
              className="dataTable"
            />
            <PaginationControls {...pagination} />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setFilterColumn("all");
                setSearchTerm("");
                handleClose();
              }}
            >
              Close
            </Button>
            <Button variant="primary" onClick={submitModal}>
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <Container fluid>
        <div className="w-100 d-flex align-items-center mt-3 p-1">
          <h5>Payment</h5>
          <hr className="flex-grow-1 mx-3" />
        </div>
        <Row className="w-100 p-2 mt-1">
          <Col xs={12} md={4} className="p-2">
            <div className="w-100 border shadow-sm p-3 rounded">
              <div className="w-100 d-flex flex-column payment-card border-bottom pb-2 mb-3">
                <span className="fw-bold">Payment Method</span>
              </div>
              <Form validated={validated} onSubmit={handleAddPayment}>
                <Form.Group className="mb-3" controlId="payWith">
                  <Form.Label className="fw-bold">Pay With:</Form.Label>
                  <div className="d-flex">
                    <Form.Check
                      type="radio"
                      name="paymentType"
                      id="bank"
                      label="Bank"
                      value="Bank"
                      checked={selectedPayment === "Bank"}
                      onChange={handlePaymentMethod}
                      className="me-3"
                    />
                    <Form.Check
                      type="radio"
                      name="paymentType"
                      id="cash"
                      label="Cash"
                      value="Cash"
                      checked={selectedPayment === "Cash"}
                      onChange={handlePaymentMethod}
                      className="me-3"
                    />
                  </div>
                  {/* <div className="mt-2">
                      <input type="checkbox" checked={onlinePaymentCheckbox} onChange={handleOnlineCheck} />
                      <label>Online</label>
                    </div>
                    <div className="mt-2">
                      <input type="checkbox" checked={checkCheque} onChange={handleCheque} />
                      <label>Check</label>
                    </div> */}
                </Form.Group>
                {/* <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Payment Category{" "}
                    <span className="text-muted badge fst-italic fw-lighter">
                      (Payment / Advance Payment)
                    </span>
                  </Form.Label>
                  <Form.Select
                    onChange={(e) => {
                      setPayment_category(e.target.value);
                    }}
                    value={payment_category}
                  >
                    <option value="" disabled>
                      Select Payment Category
                    </option>
                    <option value="payment">Payment List</option>
                    {selectedPayment !== "Cash" && (
                      <option value="advance_payment">Advance Payment</option>
                    )}
                  </Form.Select>
                </Form.Group> */}

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Check Status{" "}
                    <span className="text-muted badge fst-italic fw-lighter">
                      (Check / Non-Cheque)
                    </span>
                  </Form.Label>
                  <Form.Select
                    onChange={(e) => {
                      const checkStatusValue = e.target.value;
                      setCheckOrNone(checkStatusValue);
                      // Reset the Step 1-3 account selection and its balance
                      // display whenever Check Status changes, in either
                      // direction (same reset handleSubject1Change and
                      // handlePaymentMethod already do).
                      setSubject1("");
                      setSubject2("");
                      setSubject3("");
                      setRefNumber("");
                      setCheckNumber("");
                      setBankAmount(0);
                      setAccountName("");
                      setIsSubject2Disabled(true);
                      setIsSubject3Disabled(true);
                      if (checkStatusValue === "Check") {
                        setAmount(balance_now.toFixed(2).toString());
                      }
                    }}
                    value={checkOrNone}
                  >
                    <option value="" disabled>
                      Select Cheque Status
                    </option>
                    <option value="none">Non-Cheque</option>
                    {selectedPayment !== "Cash" && (
                      <option value="Check">Cheque</option>
                    )}
                  </Form.Select>
                </Form.Group>

                {checkOrNone !== "Check" && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Step 1 / Subject <span className="text-danger">*</span>
                      <br />
                      {isVisibleLiabNote && (
                        <span
                          className="fst-italic text-danger"
                          style={{ fontSize: 9.5 }}
                        >
                          (Please be advised that this liability will result in
                          a deduction from the selected account upon collection)
                        </span>
                      )}
                    </Form.Label>

                    <Form.Select
                      onChange={handleSubject1Change}
                      value={subject1}
                      required={checkOrNone !== "Check"}
                      disabled={items.length === 0}
                    >
                      <option value="" selected disabled>
                        Select Subject 1
                      </option>
                      <option value="Account-List">Account-List</option>
                      <option value="Asset Account">Asset Account</option>
                      <option value="Liabilities Account">
                        Liabilities Account
                      </option>
                      <option value="Owner's Equity Account">
                        Owner's Equity Account
                      </option>
                      Cheque
                    </Form.Select>
                  </Form.Group>
                )}

                {checkOrNone !== "Check" && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Step 2 / Subject <span className="text-danger">*</span>
                    </Form.Label>
                    {/* <Form.Select
                        className="form-select"
                        onChange={(e) =>
                          handleSubject2Change(
                            e,
                            e.target.options[
                              e.target.selectedIndex
                            ].getAttribute("data-subject-type")
                          )
                        }
                        value={subject2}
                        disabled={isSubject2Disabled}
                        required={checkOrOnline !== "Check"}
                      >
                        <option value="" selected disabled>
                          Select Subject 2
                        </option>
                        {subject2DataList.map((option) => (
                          <option
                            key={option.id}
                            value={option.id}
                            data-subject-type={option.subject_type}
                          >
                            {option.subject_name}
                          </option>
                        ))}
                      </Form.Select> */}
                    <Select
                      options={subject2Options}
                      value={subject2}
                      onChange={(selectedOption) =>
                        handleSubject2Change(selectedOption)
                      }
                      placeholder={`Select Subject 2`}
                      styles={withDarkSelectStyles(
                        selectCustomStyles(subject2, validated),
                        isDark,
                        validated,
                      )}
                      isDisabled={isSubject2Disabled}
                      required={checkOrNone !== "Check"}
                      isSearchable
                    />
                  </Form.Group>
                )}

                {checkOrNone !== "Check" && (
                  <Form.Group className="mb-3" controlId="accountName">
                    <Form.Label className="fw-bold">
                      Accounts <span className="text-danger">*</span>
                    </Form.Label>
                    {/* <Form.Select
                        value={subject3}
                        className="p-2"
                        onChange={handleAccountChange}
                        required={checkOrOnline !== "Check"}
                        disabled={isSubject3Disabled}
                      >
                        <option value="" disabled selected>
                          Select Account name
                        </option>
                        {subject3DataList.map((data) => (
                          <option key={data.id} value={data.id}>
                            {data.account_name}
                          </option>
                        ))}
                      </Form.Select> */}
                    <Select
                      options={subject3Options}
                      value={subject3}
                      onChange={handleAccountChange}
                      placeholder={`Select Account name`}
                      styles={withDarkSelectStyles(
                        selectCustomStyles(subject3, validated),
                        isDark,
                        validated,
                      )}
                      isDisabled={isSubject3Disabled}
                      required={checkOrNone !== "Check"}
                      isSearchable
                    />
                  </Form.Group>
                )}

                {checkOrNone === "Check" && (
                  <Form.Group className="mb-3" controlId="checkNumber">
                    <Form.Label>Cheque #</Form.Label>
                    <Form.Control
                      type="text"
                      className="p-2"
                      placeholder="000000-000-0000"
                      value={checkNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        // if (/^[0-9-]*$/.test(value) && value.length <= 15) {
                        //   setCheckNumber(value);
                        // }
                        setCheckNumber(value);
                      }}
                      required={checkOrNone === "Check"}
                    />
                    <CheckNumberError
                      value={checkNumber}
                      disabled={selectedPayment !== "Bank"}
                      pending={floatPayment
                        .filter((payment) => !payment.isDeleted)
                        .map((payment) => payment.checkNumber)}
                    />
                  </Form.Group>
                )}
                {/*  commented kasi d na need ang online payment */}
                {/* {checkOrOnline === "Online" && (
                  <Form.Group className="mb-3" controlId="checkNumber">
                    <Form.Label>Reference #</Form.Label>
                    <Form.Control
                      type="text"
                      className="p-2"
                      placeholder="000000-000-0000"
                      value={refNumber}
                      onChange={(e) => setRefNumber(e.target.value)}
                    />
                  </Form.Group>
                )} */}

                <Form.Group className="mb-3" controlId="date">
                  <Form.Label>
                    Issued Cheque Date (Maturity Date){" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <DatePicker
                    selected={issueCheckDate ? new Date(issueCheckDate) : null}
                    onChange={handleIssueCheckDate}
                    onChangeRaw={handleIssueCheckDateRaw}
                    // Month list + year grid header, same as CustomDatePicker.
                    // "Today" goes through handleIssueCheckDate, so the
                    // Process Date and posted cutoff checks still apply.
                    renderCustomHeader={(headerProps) => (
                      <CustomDatePickerHeader
                        {...headerProps}
                        onToday={handleIssueCheckDate}
                      />
                    )}
                    customInput={<TypedDateInput />}
                    className="form-control p-2"
                    wrapperClassName="w-100"
                    // placeholderText="MM/dd/yy"
                    placeholderText={DATE_INPUT_FORMAT}
                    dateFormat={DATE_INPUT_FORMAT}
                    strictParsing
                    autoComplete="off"
                    required
                    disabled={selectedPayment === "Cash"}
                    minDate={
                      processDate
                        ? new Date(new Date(processDate).setHours(0, 0, 0, 0))
                        : undefined
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="amount">
                  <Form.Label>
                    Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      {currencySymbol[currencyName] || ""}
                    </InputGroup.Text>
                    <NumberInDecimals
                      className="form-control p-2"
                      required
                      placeholder="0.00"
                      value={amount}
                      decimals={2}
                      onChange={handleAmountValue}
                    />
                  </InputGroup>
                  {accessType === "Management" && accountName && (
                    <span>
                      Account Balance:{" "}
                      <strong>
                        {bankAmount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </span>
                  )}
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100 p-2"
                  disabled={balance_now === 0}
                >
                  Add Payment
                </Button>
              </Form>
            </div>
          </Col>
          <Col xs={12} md={8} className="p-2 d-flex flex-column">
            {/* payment TABS And Advance Payments */}
            <div className="w-100 border shadow-sm p-2 rounded mb-2">
              <div className="w-100 d-flex flex-column payment-card border-bottom pb-2 mb-3">
                <span className="fw-bold">Payment List</span>
              </div>
              {/* commented kasi ginawa ko na component para d ganun kahaba */}
              {/*
              <div className="mt-3">
                <Table bordered hover responsive className="fixed-inputs-table">
                  <thead>
                    <tr>
                      <th className="p-2">Type</th>
                      <th className="p-2">Account Name</th>
                      <th className="p-2">Amount</th>
                      <th className="p-2">Check Number</th>
                      <th className="p-2">Maturity Date</th>
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  {floatPayment.length > 0 ? (
                    <tbody>
                      {floatPayment.map((data, i) => (
                        <tr key={i}>
                          <td>{data.paymentMethod || "---"}</td>
                          <td>{data.accountName || "---"}</td>
                          <td>
                            {editableRow === i ? (
                              <InputGroup>
                                <InputGroup.Text>
                                  {currencySymbol[currencyName] || ""}
                                </InputGroup.Text>
                                <NumberInDecimals
                                  className="form-control"
                                  value={editedData.amount}
                                  decimals={2}
                                  onChange={handleEditAmountValue}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleSaveEdit(i)
                                  }
                                />
                              </InputGroup>
                            ) : (
                              <InputGroup>
                                <InputGroup.Text>
                                  {currencySymbol[currencyName] || ""}
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  className="pointer-events-none"
                                  value={truncateToTwoDecimals(data.amount)}
                                  readOnly
                                />
                              </InputGroup>
                            )}
                          </td>
                          <td>
                            {editableRow === i ? (
                              <>
                                <Form.Control
                                  type="text"
                                  value={editedData.checkNumber}
                                  onChange={(e) =>
                                    setEditedData((prev) => ({
                                      ...prev,
                                      checkNumber: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleSaveEdit(i)
                                  }
                                  placeholder="---"
                                />
                                <CheckNumberError
                                  value={editedData.checkNumber}
                                  ignore={[data.checkNumber]}
                                  pending={floatPayment
                                    .filter(
                                      (payment, j) => j !== i && !payment.isDeleted,
                                    )
                                    .map((payment) => payment.checkNumber)}
                                />
                              </>
                            ) : (
                              <Form.Control
                                type="text"
                                className="pointer-events-none"
                                value={data.checkNumber || "---"}
                                readOnly
                              />
                            )}
                          </td>
                      
                          <td>
                            <CustomDatePicker
                              index={i}
                              label={"Issued Check Date"}
                              selected={
                                editableRow === i
                                  ? editedData.issuedDate
                                    ? new Date(editedData.issuedDate)
                                    : null
                                  : data.issuedDate
                                    ? new Date(data.issuedDate)
                                    : null
                              }
                              handleDateChange={(date) =>
                                handleEditIssuedDate(date, i)
                              }
                              CustomInput={CustomInput}
                              isRequired={true}
                              validated={validated}
                            />
                          </td>
                          
                          <td className="text-center d-flex">
                            {editableRow === i ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  className="me-1"
                                  onClick={() => handleSaveEdit(i)}
                                  disabled={balance_now < 0}
                                  title="Save Changes"
                                >
                                  <i className="fa-solid fa-check"></i>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => setEditableRow(null)}
                                  title="Cancel"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-1"
                                  onClick={() => handleEditClick(i, data)}
                                  title="Edit Payment"
                                >
                                  <i className="fa-solid fa-edit"></i>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeletePayment(i)}
                                  title="Delete Payment"
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <caption className="text-center mt-3">
                      There are no records to display
                    </caption>
                  )}
                </Table>
              </div> */}
              {/* <Tabs
                transition={false}
                mountOnEnter
                unmountOnExit={false}
                activeKey={activeTab}
                onSelect={handleTabSelect}
                id="status-tabs"
                className="mb-3"
              >
                {" "}
                <Tab eventKey="payments_list" title="Payment List">
                  {" "}
                  <BCPayments
                    floatPayment={floatPayment}
                    editableRow={editableRow}
                    editedData={editedData}
                    currencyName={currencyName}
                    currencySymbol={currencySymbol}
                    balance_now={balance_now}
                    validated={validated}
                    CustomInput={CustomInput}
                    CustomDatePicker={CustomDatePicker}
                    handleEditAmountValue={handleEditAmountValue}
                    handleSaveEdit={handleSaveEdit}
                    handleEditClick={handleEditClick}
                    handleDeletePayment={handleDeletePayment}
                    handleEditIssuedDate={handleEditIssuedDate}
                    setEditableRow={setEditableRow}
                    setEditedData={setEditedData}
                  />
                </Tab>
                <Tab eventKey="advance_payments" title="Advance Payments">
                  {" "}
                  <BCPayments
                    floatPayment={floatPayment}
                    editableRow={editableRow}
                    editedData={editedData}
                    currencyName={currencyName}
                    currencySymbol={currencySymbol}
                    balance_now={balance_now}
                    validated={validated}
                    CustomInput={CustomInput}
                    CustomDatePicker={CustomDatePicker}
                    handleEditAmountValue={handleEditAmountValue}
                    handleSaveEdit={handleSaveEdit}
                    handleEditClick={handleEditClick}
                    handleDeletePayment={handleDeletePayment}
                    handleEditIssuedDate={handleEditIssuedDate}
                    setEditableRow={setEditableRow}
                    setEditedData={setEditedData}
                  />
                </Tab>
              </Tabs> */}

              <BCPayments
                floatPayment={floatPayment}
                editableRow={editableRow}
                editedData={editedData}
                currencyName={currencyName}
                currencySymbol={currencySymbol}
                balance_now={balance_now}
                validated={validated}
                CustomInput={CustomInput}
                CustomDatePicker={CustomDatePicker}
                handleEditAmountValue={handleEditAmountValue}
                handleSaveEdit={handleSaveEdit}
                handleEditClick={handleEditClick}
                handleDeletePayment={handleDeletePayment}
                handleEditIssuedDate={handleEditIssuedDate}
                setEditableRow={setEditableRow}
                setEditedData={setEditedData}
              />
            </div>

            {/* balance Details */}
            <div className="w-100 border shadow-sm p-3 rounded">
              <div className="w-100 d-flex flex-column payment-card border-bottom pb-2 mb-3">
                <span className="fw-bold">Balance Details</span>
              </div>
              <Row>
                {/* Left Side: Order List Summary */}
                <Col xs={12} lg={5} className="mb-4 mb-lg-0">
                  <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Gross Amount :</span>
                    <span className="text-secondary">
                      {`${currencySymbol[currencyName] || ""} ${truncateToTwoDecimals(totalAmountSum)}`}
                    </span>
                  </div>
                  <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Overall Discount :</span>
                    <span
                      className={`${
                        overAllDiscount.convertedAmount > 0
                          ? "text-danger"
                          : overAllDiscount.convertedAmount < 0
                            ? "text-success"
                            : "text-secondary"
                      }`}
                    >
                      {`${currencySymbol[currencyName] || ""} ${truncateToTwoDecimals(Math.abs(overAllDiscount.convertedAmount))}`}
                    </span>
                  </div>

                  <div className="w-100 d-flex flex-row justify-content-between p-3 mt-3 total-amount-container align-items-center rounded">
                    <span className="text-white">Receivable Balance</span>
                    <span className="text-white text-underline">
                      {`${currencySymbol[currencyName] || ""} ${truncateToTwoDecimals(balance_now)}`}
                    </span>
                  </div>
                  <div className="w-100 d-flex flex-row justify-content-between p-3 mt-3 total-amount-container align-items-center rounded">
                    <span className="text-white">Advance Payment</span>
                    <span className="text-white text-underline">
                      {`${currencySymbol[currencyName] || ""} ${truncateToTwoDecimals(advance_payment)}`}
                    </span>
                  </div>
                </Col>

                {/* Spacer block (hidden on mobile, visible on large screens) */}
                <Col lg={2} className="d-none d-lg-block"></Col>

                {/* Right Side: Payment Summary */}
                <Col xs={12} lg={5}>
                  <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Cash</span>
                    <span className="text-secondary">
                      {truncateToTwoDecimals(totalCashPaid)}
                    </span>
                  </div>
                  <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Bank</span>
                    <span className="text-secondary">
                      {truncateToTwoDecimals(totalBankPaid)}
                    </span>
                  </div>
                  <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Check</span>
                    <span className="text-secondary">
                      {truncateToTwoDecimals(totalCheckPaid)}
                    </span>
                  </div>
                  {/* <div className="w-100 d-flex flex-row justify-content-between p-2">
                    <span>Online</span>
                    <span className="text-secondary">
                      {truncateToTwoDecimals(totalOnlinePaid)}
                    </span>
                  </div> */}
                  <div className="w-100 d-flex flex-row justify-content-between p-3 mt-3 total-amount-container align-items-center rounded">
                    <span className="text-white">Total Payment</span>
                    <span className="text-white text-underline">
                      {`${
                        currencySymbol[currencyName] || ""
                      } ${truncateToTwoDecimals(totalPayment)}`}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            <Row className="mt-4">
              <Col sm></Col>
              <Col sm></Col>
              <Col sm className="align-items-end mb-2 w-100">
                {balance_now < 0 && (
                  <span className="fs-6 text-danger mb-2">
                    Balance cannot be less than zero
                  </span>
                )}
                <Col sm className="d-flex flex-row">
                  <Button
                    variant="outline-secondary"
                    className="w-100 me-3"
                    onClick={() => {
                      if (checkEditPayment()) return;
                      navigate(
                        isLocal
                          ? "/sales/local-collections"
                          : "/sales/local-collections",
                      );
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="w-100"
                    type="button"
                    disabled={balance_now < 0}
                    onClick={handleSavePayment}
                  >
                    Save To
                  </Button>
                </Col>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BulkCollection;
